import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Empty,
  Popconfirm,
  message,
  Divider,
  Spin,
} from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  CloseOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { startCase } from 'lodash';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { useUserContext } from '../../../../../core/components/users/useUserContext';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import TeamSettingsModal from '../teams/TeamSettingsModal';
import { space, radius, shadow, text, fontSize, fontWeight, border, neutral } from '../../configs/designTokens';
import { ENABLED_USER_FILTER } from '../../../../../core/utils/enabledUserFilter';

const { Option } = Select;

const TeamsTable = ({ createModalVisible, setCreateModalVisible }) => {
  const {
    currentUser,
    fetchTeams,
    teamsData,
    teamsLoading,
    createTeam,
    createTeamLoading,
    updateTeam,
    updateTeamLoading,
  } = useTaskContext();

  const { fetchBasicUsersDetails, basicUsersDetails, basicUsersDetailsLoading } = useUserContext();

  const [createForm] = Form.useForm();
  const [subscribeForm] = Form.useForm();

  // Create modal visibility is controlled by parent via props

  // Subscribe modal
  const [subscribeModalVisible, setSubscribeModalVisible] = useState(false);
  const [subscribeTeam, setSubscribeTeam] = useState(null);

  // Team settings modal — opened from a card's header gear
  const [settingsTeam, setSettingsTeam] = useState(null);

  // Subscribers modal — who follows a given team (incoming teams + users).
  // Store the id and derive the live team from `teams` so removals reflect after refetch.
  const [subscribersModalTeamId, setSubscribersModalTeamId] = useState(null);

  const userOrgId = currentUser?.role?.org?.id;

  const refetchTeams = () => {
    if (userOrgId) {
      fetchTeams({
        variables: {
          where: { org: { id: userOrgId } },
          orderBy: 'name_ASC',
        },
      });
    }
  };

  useEffect(() => {
    refetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOrgId]);

  const teams = (teamsData?.teams || []).filter((t) => !t.name.startsWith('__deleted__'));

  // Live team for the Subscribers modal (recomputed from `teams` on every refetch).
  const subscribersTeam = subscribersModalTeamId
    ? teams.find((t) => t.id === subscribersModalTeamId) || null
    : null;

  const fetchUsersForOrg = (name = '') => {
    fetchBasicUsersDetails({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: { id: userOrgId, level: 'PLATFORM' },
                roleItems_some: { feature: 'TASK_MANAGEMENT' },
              },
              name_contains: name,
            },
            ENABLED_USER_FILTER,
          ],
        },
      },
    });
  };

  // ============================================
  // CREATE TEAM
  // ============================================

  useEffect(() => {
    if (createModalVisible) {
      createForm.resetFields();
      fetchUsersForOrg();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createModalVisible]);

  const handleCreateSubmit = async () => {
    try {
      const values = await createForm.validateFields();
      const {
        name,
        description,
        memberIds,
        subscriberTeamIds,
        subscriberUserIds,
      } = values;

      // The creator is always added to the team as a member
      const allMemberIds = [
        currentUser.id,
        ...(memberIds || []).filter((id) => id !== currentUser.id),
      ];

      const data = {
        name,
        description,
        org: { connect: { id: userOrgId } },
        createdBy: { connect: { id: currentUser.id } },
        members: {
          connect: allMemberIds.map((id) => ({ id })),
        },
      };

      // Individual users who should subscribe to this team from the start
      if (subscriberUserIds && subscriberUserIds.length > 0) {
        data.subscribers = {
          connect: subscriberUserIds.map((id) => ({ id })),
        };
      }

      const result = await createTeam({ variables: { data } });
      const newTeamId = result?.data?.createTeam?.id;

      // Existing teams that should subscribe to the new team.
      // A team's outgoing follows live on its own `subscribedTeams`, so we
      // connect the new team onto each chosen source team (same path the
      // subscribe modal uses).
      if (newTeamId && subscriberTeamIds && subscriberTeamIds.length > 0) {
        await Promise.all(
          subscriberTeamIds.map((sourceTeamId) =>
            updateTeam({
              variables: {
                data: { subscribedTeams: { connect: [{ id: newTeamId }] } },
                where: { id: sourceTeamId },
              },
            })
          )
        );
      }

      message.success('Team created successfully');
      setCreateModalVisible(false);
      createForm.resetFields();
      refetchTeams();
    } catch (error) {
      if (error.errorFields) return;
      message.error('Failed to create team: ' + error.message);
    }
  };

  // ============================================
  // SUBSCRIBE / UNSUBSCRIBE
  // ============================================

  // For a given target team, figure out which subscribe options are still open:
  //   - canIndividual: I'm not already individually subscribed
  //   - availableSourceTeams: any team that isn't the target and hasn't
  //     already subscribed to it (anyone can wire up team-to-team subscriptions)
  const getSubscribeOptions = (targetTeam) => {
    if (!targetTeam) {
      return { canIndividual: true, availableSourceTeams: teams };
    }
    const alreadyIndividual = (targetTeam.subscribers || []).some(
      (s) => s.id === currentUser?.id
    );
    const usedSourceIds = new Set();
    teams.forEach((srcTeam) => {
      if ((srcTeam.subscribedTeams || []).some((st) => st.id === targetTeam.id)) {
        usedSourceIds.add(srcTeam.id);
      }
    });
    return {
      canIndividual: !alreadyIndividual,
      availableSourceTeams: teams.filter(
        (t) => t.id !== targetTeam.id && !usedSourceIds.has(t.id)
      ),
    };
  };

  const handleOpenSubscribe = (targetTeam) => {
    setSubscribeTeam(targetTeam);
    subscribeForm.resetFields();
    const opts = getSubscribeOptions(targetTeam);
    const defaultType = opts.canIndividual
      ? 'individual'
      : opts.availableSourceTeams.length > 0
        ? 'team'
        : 'individual';
    subscribeForm.setFieldsValue({ subscriptionType: defaultType });
    setSubscribeModalVisible(true);
  };

  const handleSubscribeSubmit = async () => {
    try {
      const values = await subscribeForm.validateFields();
      const { subscriptionType, sourceTeamId } = values;

      if (subscriptionType === 'individual') {
        // Add current user as individual subscriber to the target team
        await updateTeam({
          variables: {
            data: { subscribers: { connect: [{ id: currentUser.id }] } },
            where: { id: subscribeTeam.id },
          },
        });
        message.success(`You are now subscribed to "${subscribeTeam.name}"`);
      } else {
        // Subscribe source team to the target team
        if (!sourceTeamId) {
          message.warning('Please select which team to subscribe');
          return;
        }
        await updateTeam({
          variables: {
            data: { subscribedTeams: { connect: [{ id: subscribeTeam.id }] } },
            where: { id: sourceTeamId },
          },
        });
        const sourceTeam = teams.find((t) => t.id === sourceTeamId);
        message.success(`Team "${sourceTeam?.name}" is now subscribed to "${subscribeTeam.name}"`);
      }

      setSubscribeModalVisible(false);
      subscribeForm.resetFields();
      refetchTeams();
    } catch (error) {
      if (error.errorFields) return;
      message.error('Failed to subscribe: ' + error.message);
    }
  };

  const handleUnsubscribeIndividual = async (targetTeam) => {
    try {
      await updateTeam({
        variables: {
          data: { subscribers: { disconnect: [{ id: currentUser.id }] } },
          where: { id: targetTeam.id },
        },
      });
      message.success(`Unsubscribed from "${targetTeam.name}"`);
      refetchTeams();
    } catch (error) {
      message.error('Failed to unsubscribe: ' + error.message);
    }
  };

  const handleUnsubscribeTeam = async (targetTeam, sourceTeamId) => {
    try {
      await updateTeam({
        variables: {
          data: { subscribedTeams: { disconnect: [{ id: targetTeam.id }] } },
          where: { id: sourceTeamId },
        },
      });
      message.success(`Team unsubscribed from "${targetTeam.name}"`);
      refetchTeams();
    } catch (error) {
      message.error('Failed to unsubscribe: ' + error.message);
    }
  };

  // Remove ANY individual subscriber from a team (used by the Subscribers modal).
  const handleRemoveSubscriber = async (targetTeam, userId) => {
    try {
      await updateTeam({
        variables: {
          data: { subscribers: { disconnect: [{ id: userId }] } },
          where: { id: targetTeam.id },
        },
      });
      message.success('Subscriber removed');
      refetchTeams();
    } catch (error) {
      message.error('Failed to remove subscriber: ' + error.message);
    }
  };

  // ============================================
  // RENDER — Team Cards
  // ============================================

  const userId = currentUser?.id;

  // Categorize teams into:
  //   myTeams:           Teams where I am a member
  //   mySubscribedTeams: Teams I actively subscribed to (individually or via my team)
  //                      NOTE: teams that subscribe TO us do NOT appear here
  //   otherOrgTeams:     Everything else in the org
  const { myTeams, mySubscribedTeams, otherOrgTeams } = useMemo(() => {
    const mine = [];
    const subscribed = [];
    const other = [];

    // 1. Find teams I belong to
    const myTeamIds = new Set();
    teams.forEach((t) => {
      if (t.members?.some((m) => m.id === userId)) {
        myTeamIds.add(t.id);
      }
    });

    // 2. Map each target team -> list of MY teams that subscribe to it.
    //    A target may have multiple sources (e.g. I lead Team A and Team B,
    //    both subscribed to Team Z). subscribedTeams = outgoing follows.
    const subscribedViaTeamMap = {};
    teams.forEach((t) => {
      if (myTeamIds.has(t.id)) {
        (t.subscribedTeams || []).forEach((st) => {
          if (!subscribedViaTeamMap[st.id]) subscribedViaTeamMap[st.id] = [];
          subscribedViaTeamMap[st.id].push(t.id);
        });
      }
    });

    // 3. Categorize each team
    teams.forEach((t) => {
      const isMember = t.members?.some((m) => m.id === userId);
      const isIndividualSubscriber = t.subscribers?.some((s) => s.id === userId);
      const sourceTeamIds = subscribedViaTeamMap[t.id] || [];
      const isSubscribedViaTeam = sourceTeamIds.length > 0 && !isMember;

      if (isMember) {
        mine.push(t);
      } else if (isIndividualSubscriber || isSubscribedViaTeam) {
        subscribed.push({
          ...t,
          _isIndividualSubscriber: isIndividualSubscriber,
          _sourceTeamIds: sourceTeamIds,
        });
      } else {
        other.push(t);
      }
    });

    return { myTeams: mine, mySubscribedTeams: subscribed, otherOrgTeams: other };
  }, [teams, userId]);

  const containerStyle = {
    background: '#fff',
    minHeight: 'calc(100vh - 300px)',
    borderRadius: radius.lg,
    padding: space[4],
  };

  // Reusable card renderer
  const renderTeamCard = (team, options = {}) => {
    const { showIncomingSubscribers, hideSubscribeButton } = options;
    const isIndividualSubscriber = !!team._isIndividualSubscriber;
    const sourceTeamIds = team._sourceTeamIds || [];
    const hasAnySubscription = isIndividualSubscriber || sourceTeamIds.length > 0;
    return (
      <div key={team.id} style={{ flex: '1 1 300px', maxWidth: 380, display: 'flex' }}>
        <Card
          hoverable
          style={{
            borderRadius: radius.lg,
            boxShadow: shadow.sm,
            width: '100%',
            height: '100%',
          }}
          bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          {/* Card Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: space[2],
              padding: `${space[4]}px ${space[5]}px`,
              borderRadius: `${radius.lg}px ${radius.lg}px 0 0`,
            }}
            className="team-card__header"
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: text.primary, marginBottom: 2 }}>
                <TeamOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                {team.name}
                {hasAnySubscription && (
                  <Tag color="blue" style={{ marginLeft: 8, fontSize: 10, verticalAlign: 'middle' }}>
                    Subscribed
                  </Tag>
                )}
              </div>
              <div style={{ fontSize: 13, color: text.secondary, minHeight: 20 }}>
                {team.description || 'No description'}
              </div>
            </div>
            <Button
              type="text"
              size="small"
              icon={<SettingOutlined />}
              title="Team settings"
              aria-label="Team settings"
              onClick={() => setSettingsTeam(team)}
              style={{ flexShrink: 0, color: text.secondary }}
            />
          </div>

          {/* Card Body */}
          <div style={{ padding: `0 ${space[5]}px ${space[5]}px`, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Divider style={{ margin: `0 0 ${space[4]}px 0` }} />

            {/* Members */}
            <div style={{ marginBottom: space[4] }}>
              <div style={{ fontSize: 12, color: text.label, marginBottom: space[2], textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <UserOutlined style={{ marginRight: 4 }} /> Members ({team.members?.length || 0})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: space[2] }}>
                {team.members?.slice(0, 6).map((member) => (
                  <div
                    key={member.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: space[2],
                      padding: `${space[1]}px ${space[2]}px`,
                      background: neutral[50],
                      borderRadius: radius.md,
                    }}
                  >
                    <AvatarS3
                      imgKey={member.avatar?.key}
                      username={member.name || member.username}
                      avatarProps={{ size: 28 }}
                    />
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 13,
                        fontWeight: 500,
                        color: text.primary,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {startCase(member.name || member.username)}
                    </span>
                  </div>
                ))}
                {team.members?.length > 6 && (
                  <span style={{ fontSize: 12, color: text.label, paddingLeft: space[1] }}>
                    +{team.members.length - 6} more
                  </span>
                )}
                {(!team.members || team.members.length === 0) && (
                  <span style={{ fontSize: 13, color: text.muted }}>No members</span>
                )}
              </div>
            </div>

            {/* Subscriptions — only show "Subscribed to" on non-myTeams tabs */}
            {!showIncomingSubscribers && team.subscribedTeams?.length > 0 && (
              <div style={{ marginBottom: space[3] }}>
                <span style={{ fontSize: 11, color: text.label }}>Subscribed to: </span>
                {team.subscribedTeams.map((t) => (
                  <Tag key={t.id} color="blue" style={{ fontSize: 11 }}>{t.name}</Tag>
                ))}
              </div>
            )}

            {/* Footer — pinned to the card bottom: your action (left) + subscribers (right) */}
            <div
              style={{
                marginTop: 'auto',
                paddingTop: space[3],
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                gap: space[2],
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Your subscriptions — one tag per source, plus add-another */}
                {hasAnySubscription ? (() => {
                  const opts = getSubscribeOptions(team);
                  const canAddMore = opts.canIndividual || opts.availableSourceTeams.length > 0;
                  return (
                    <>
                      <div style={{ fontSize: 12, color: text.label, marginBottom: space[2], textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Your subscriptions
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                        {isIndividualSubscriber && (
                          <Popconfirm
                            title={`Unsubscribe yourself from "${team.name}"?`}
                            onConfirm={() => handleUnsubscribeIndividual(team)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Tag color="purple" style={{ cursor: 'pointer', fontSize: 11 }}>
                              <UserOutlined /> You <CloseOutlined style={{ fontSize: 9, marginLeft: 2 }} />
                            </Tag>
                          </Popconfirm>
                        )}
                        {sourceTeamIds.map((srcId) => {
                          const src = teams.find((t) => t.id === srcId);
                          if (!src) return null;
                          return (
                            <Popconfirm
                              key={srcId}
                              title={`Unsubscribe "${src.name}" from "${team.name}"?`}
                              onConfirm={() => handleUnsubscribeTeam(team, srcId)}
                              okText="Yes"
                              cancelText="No"
                            >
                              <Tag color="blue" style={{ cursor: 'pointer', fontSize: 11 }}>
                                Via {src.name} <CloseOutlined style={{ fontSize: 9, marginLeft: 2 }} />
                              </Tag>
                            </Popconfirm>
                          );
                        })}
                        {canAddMore && (
                          <Button
                            size="small"
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() => handleOpenSubscribe(team)}
                            style={{ fontSize: 11, height: 22, padding: '0 8px' }}
                          >
                            Add
                          </Button>
                        )}
                      </div>
                    </>
                  );
                })() : (!hideSubscribeButton ? (
                  <Button
                    size="small"
                    style={{
                      background: '#e6f7ff',
                      borderColor: '#91d5ff',
                      color: '#1890ff',
                      fontSize: 12,
                      fontWeight: 500,
                      borderRadius: radius.sm,
                    }}
                    onClick={() => handleOpenSubscribe(team)}
                  >
                    <TeamOutlined /> Subscribe
                  </Button>
                ) : null)}
              </div>

              {/* Who subscribed to this team (incoming teams + individuals) */}
              {(() => {
                const subTeams = team.subscriberTeams || [];
                const subUsers = team.subscribers || [];
                const subCount = subTeams.length + subUsers.length;
                if (subCount === 0) {
                  return (
                    <span style={{ fontSize: 11, color: text.muted, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      No subscribers
                    </span>
                  );
                }
                return (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSubscribersModalTeamId(team.id);
                    }}
                    title="See who subscribed to this team"
                    style={{
                      cursor: 'pointer',
                      flexShrink: 0,
                      fontSize: 11,
                      fontWeight: 500,
                      color: text.secondary,
                      textDecoration: 'underline',
                      textUnderlineOffset: 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {subCount} subscriber{subCount === 1 ? '' : 's'}
                  </span>
                );
              })()}
            </div>
          </div>
        </Card>
      </div>
    );
  };

  const renderCardGrid = (teamsList, emptyMessage, options = {}) => {
    if (teamsList.length === 0) {
      return (
        <Empty
          description={emptyMessage}
          style={{ padding: space[10] }}
        />
      );
    }
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: space[4] }}>
        {teamsList.map((team) => renderTeamCard(team, {
          showIncomingSubscribers: options.showIncomingSubscribers,
          hideSubscribeButton: options.hideSubscribeButton,
        }))}
      </div>
    );
  };

  if (teamsLoading && teams.length === 0) {
    return (
      <div style={{ ...containerStyle, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* ============================================ */}
      {/* SUMMARY — how teams work                     */}
      {/* ============================================ */}
      <div
        style={{
          marginBottom: space[5],
          paddingBottom: space[4],
          borderBottom: `1px solid ${border.light}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: space[2],
            fontSize: fontSize.title,
            fontWeight: fontWeight.semibold,
            color: text.primary,
            marginBottom: space[2],
          }}
        >
          <TeamOutlined /> Teams
        </div>
        <p
          style={{
            fontSize: fontSize.meta,
            color: text.secondary,
            lineHeight: 1.6,
            maxWidth: 720,
            marginBottom: 0,
          }}
        >
          Teams group people together so tasks can be assigned to a whole team instead of a single
          person. Everyone on a team is an equal member. Subscribe to a team — on your own or with
          another of your teams — to follow its work and stay in the loop even if you&apos;re not a
          member. Below you&apos;ll find the teams you belong to, teams you&apos;ve subscribed to,
          and other teams in your organization.
        </p>
      </div>

      {teams.length === 0 ? (
        <Empty
          description="No teams yet. Create your first team to get started."
          style={{ padding: space[10] }}
        />
      ) : (
        <>
          {/* Own teams — member */}
          {myTeams.length > 0 && (
            <div style={{ marginBottom: space[6] }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: text.primary, marginBottom: space[3] }}>
                <UserOutlined style={{ marginRight: 6 }} /> Teams I belong to ({myTeams.length})
              </div>
              {renderCardGrid(myTeams, '', { showIncomingSubscribers: true, hideSubscribeButton: true })}
            </div>
          )}

          {/* Teams I subscribed to */}
          {mySubscribedTeams.length > 0 && (
            <div style={{ marginBottom: space[6] }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: text.primary, marginBottom: space[3] }}>
                <TeamOutlined style={{ marginRight: 6 }} /> Teams I'm subscribed to ({mySubscribedTeams.length})
              </div>
              {renderCardGrid(mySubscribedTeams, '', { showIncomingSubscribers: true, hideSubscribeButton: true })}
            </div>
          )}

          {/* Other teams in the org */}
          {otherOrgTeams.length > 0 && (
            <div style={{ marginBottom: space[6] }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: text.primary, marginBottom: space[3] }}>
                <TeamOutlined style={{ marginRight: 6 }} /> Other Teams ({otherOrgTeams.length})
              </div>
              {renderCardGrid(otherOrgTeams, '', { showIncomingSubscribers: true })}
            </div>
          )}
        </>
      )}

      {/* ============================================ */}
      {/* CREATE TEAM MODAL — Simple form, equal members */}
      {/* ============================================ */}
      <Modal
        className="task-create-modal"
        title="Create Team"
        visible={createModalVisible}
        onOk={handleCreateSubmit}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        confirmLoading={createTeamLoading || updateTeamLoading}
        width={500}
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="name"
            label="Team Name"
            rules={[
              { required: true, message: 'Please enter a team name' },
              {
                validator: (_, value) => {
                  if (value && teams.some((t) => t.name.toLowerCase() === value.trim().toLowerCase())) {
                    return Promise.reject(new Error('A team with this name already exists'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="e.g. Applications, DevOps, Integrations" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea placeholder="Brief description of this team" rows={2} maxLength={95} />
          </Form.Item>

          <Form.Item
            name="memberIds"
            label="Members"
            extra="You're added to this team automatically as a member."
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="Select members to add"
              loading={basicUsersDetailsLoading}
              onFocus={() => fetchUsersForOrg()}
              onSearch={(searchText) => fetchUsersForOrg(searchText)}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {basicUsersDetails
                .filter((user) => user.id !== currentUser.id)
                .map((user) => (
                  <Option key={user.id} value={user.id}>
                    {startCase(user.name || user.username)}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Divider style={{ margin: `${space[2]}px 0 ${space[4]}px` }} />

          <div style={{ fontSize: 13, fontWeight: 600, color: text.primary, marginBottom: space[3] }}>
            Subscriptions (optional)
          </div>

          <Form.Item
            name="subscriberTeamIds"
            label="Subscribe an entire team"
            extra="Existing teams that should follow this team's tasks."
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder={
                teams.length === 0
                  ? 'No other teams to subscribe yet'
                  : 'Select teams to subscribe'
              }
              disabled={teams.length === 0}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {teams.map((t) => (
                <Option key={t.id} value={t.id}>
                  {t.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="subscriberUserIds"
            label="Subscribe specific users"
            extra="Individual people who should follow this team, without subscribing their whole team."
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="Select users to subscribe"
              loading={basicUsersDetailsLoading}
              onFocus={() => fetchUsersForOrg()}
              onSearch={(searchText) => fetchUsersForOrg(searchText)}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {/* select-options-ok: add-only picker, the form never seeds existing subscribers */}
              {basicUsersDetails.map((user) => (
                <Option key={user.id} value={user.id}>
                  {startCase(user.name || user.username)}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* ============================================ */}
      {/* SUBSCRIBE MODAL                              */}
      {/* ============================================ */}
      <Modal
        className="task-create-modal"
        title={`Subscribe to "${subscribeTeam?.name || ''}"`}
        visible={subscribeModalVisible}
        onOk={handleSubscribeSubmit}
        onCancel={() => {
          setSubscribeModalVisible(false);
          subscribeForm.resetFields();
        }}
        confirmLoading={updateTeamLoading}
        width={500}
      >
        <Form form={subscribeForm} layout="vertical">
          <div
            style={{
              fontSize: fontSize.meta,
              color: text.secondary,
              lineHeight: 1.6,
              background: neutral[50],
              border: `1px solid ${border.light}`,
              borderRadius: radius.md,
              padding: `${space[2]}px ${space[3]}px`,
              marginBottom: space[4],
            }}
          >
            Subscribing means you follow this team&apos;s work — subscribers are automatically added
            as collaborators on its new tasks, so they stay in the loop. Pick{' '}
            <strong>Just me</strong> to subscribe yourself, or <strong>With a team</strong> to
            subscribe an entire team (every member of that team follows along).
          </div>
          {(() => {
            const opts = subscribeTeam
              ? getSubscribeOptions(subscribeTeam)
              : { canIndividual: true, availableSourceTeams: teams };
            return (
              <>
                <Form.Item
                  name="subscriptionType"
                  label="How would you like to subscribe?"
                  rules={[{ required: true, message: 'Please select how to subscribe' }]}
                >
                  <Select
                    onChange={() => subscribeForm.setFieldsValue({ sourceTeamId: undefined })}
                  >
                    <Option value="individual" disabled={!opts.canIndividual}>
                      {opts.canIndividual
                        ? "Just me — I personally get notified about this team's tasks"
                        : "Just me — already subscribed individually"}
                    </Option>
                    <Option value="team" disabled={opts.availableSourceTeams.length === 0}>
                      {opts.availableSourceTeams.length === 0
                        ? 'With a team — every team already subscribes here'
                        : 'With a team — subscribe a team to collaborate'}
                    </Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) => prev.subscriptionType !== cur.subscriptionType}
                >
                  {() => {
                    const type = subscribeForm.getFieldValue('subscriptionType');
                    if (type !== 'team') return null;
                    return (
                      <Form.Item
                        name="sourceTeamId"
                        label="Which team should subscribe?"
                        rules={[{ required: true, message: 'Please select a team' }]}
                      >
                        <Select
                          showSearch
                          placeholder="Select a team"
                          filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {opts.availableSourceTeams.map((t) => (
                            <Option key={t.id} value={t.id}>
                              {t.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </>
            );
          })()}
        </Form>
      </Modal>

      {/* ============================================ */}
      {/* SUBSCRIBERS MODAL — who follows this team    */}
      {/* ============================================ */}
      <Modal
        className="task-create-modal"
        title={
          subscribersTeam
            ? `Subscribers of "${subscribersTeam.name}"`
            : 'Subscribers'
        }
        visible={!!subscribersTeam}
        onCancel={() => setSubscribersModalTeamId(null)}
        footer={[
          <Button key="close" onClick={() => setSubscribersModalTeamId(null)}>
            Close
          </Button>,
        ]}
        width={500}
      >
        {subscribersTeam && (() => {
          const subTeams = subscribersTeam.subscriberTeams || [];
          const subUsers = subscribersTeam.subscribers || [];
          if (subTeams.length === 0 && subUsers.length === 0) {
            return <Empty description="No one is subscribed to this team yet." />;
          }
          return (
            <>
              <div style={{ fontSize: fontSize.meta, color: text.secondary, lineHeight: 1.6, marginBottom: space[4] }}>
                These teams and people follow <strong>{subscribersTeam.name}</strong> — they&apos;re
                automatically added as collaborators on its new tasks. Remove anyone below to stop them following.
              </div>

              {/* Subscriber teams + their members */}
              {subTeams.length > 0 && (
                <div style={{ marginBottom: subUsers.length > 0 ? space[5] : 0 }}>
                  <div style={{ fontSize: 12, color: text.label, marginBottom: space[2], textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    <TeamOutlined style={{ marginRight: 6 }} /> Teams ({subTeams.length})
                  </div>
                  {subTeams.map((t) => (
                    <div
                      key={t.id}
                      style={{
                        border: `1px solid ${border.light}`,
                        borderRadius: radius.md,
                        padding: space[3],
                        marginBottom: space[2],
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: space[2], marginBottom: space[2] }}>
                        <div style={{ fontWeight: 600, color: text.primary }}>
                          <TeamOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                          {t.name}
                          <span style={{ fontWeight: 400, color: text.muted, marginLeft: 6 }}>
                            ({t.members?.length || 0} member{(t.members?.length || 0) === 1 ? '' : 's'})
                          </span>
                        </div>
                        <Popconfirm
                          title={`Remove "${t.name}" from this team's subscribers?`}
                          onConfirm={() => handleUnsubscribeTeam(subscribersTeam, t.id)}
                          okText="Remove"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<CloseOutlined />}
                            aria-label={`Remove ${t.name}`}
                            style={{ flexShrink: 0 }}
                          />
                        </Popconfirm>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: space[2] }}>
                        {(t.members || []).map((m) => (
                          <div
                            key={m.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: `${space[1]}px ${space[2]}px`,
                              background: neutral[50],
                              borderRadius: radius.md,
                            }}
                          >
                            <AvatarS3
                              imgKey={m.avatar?.key}
                              username={m.name || m.username}
                              avatarProps={{ size: 22 }}
                            />
                            <span style={{ fontSize: 12, fontWeight: 500 }}>
                              {startCase(m.name || m.username)}
                            </span>
                          </div>
                        ))}
                        {(!t.members || t.members.length === 0) && (
                          <span style={{ fontSize: 12, color: text.muted }}>No members</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Individual subscribers */}
              {subUsers.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: text.label, marginBottom: space[2], textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    <UserOutlined style={{ marginRight: 6 }} /> Individual subscribers ({subUsers.length})
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: space[2] }}>
                    {subUsers.map((u) => (
                      <div
                        key={u.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: `${space[1]}px ${space[2]}px`,
                          background: '#f5f3ff',
                          border: '1px solid #e9d5ff',
                          borderRadius: radius.md,
                        }}
                      >
                        <AvatarS3
                          imgKey={u.avatar?.key}
                          username={u.name || u.username}
                          avatarProps={{ size: 22 }}
                        />
                        <span style={{ fontSize: 12, fontWeight: 500 }}>
                          {startCase(u.name || u.username)}
                        </span>
                        <Popconfirm
                          title={`Remove ${startCase(u.name || u.username)} from subscribers?`}
                          onConfirm={() => handleRemoveSubscriber(subscribersTeam, u.id)}
                          okText="Remove"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true }}
                        >
                          <CloseOutlined
                            role="button"
                            aria-label={`Remove ${startCase(u.name || u.username)}`}
                            style={{ fontSize: 10, marginLeft: 2, color: text.muted, cursor: 'pointer' }}
                          />
                        </Popconfirm>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </Modal>

      {/* ============================================ */}
      {/* TEAM SETTINGS MODAL — opened from card gear  */}
      {/* ============================================ */}
      <TeamSettingsModal
        team={settingsTeam}
        visible={!!settingsTeam}
        onClose={() => setSettingsTeam(null)}
        onChanged={refetchTeams}
        allTeams={teams}
      />
    </div>
  );
};

export default TeamsTable;
